import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useHasRole } from '~/shared/context/use-has-role';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { companySettingsKeys } from '../../company-settings/api/query-keys';
import type { InvitationMethod } from '../../company-settings/api/user-management';
import { useInvitationOptions } from '../../company-settings/api/user-management/use-invitation-options';
import type { InviteTeamMemberFormData } from '../../company-settings/tabs/organization-tab/users-and-roles/types';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const JOB_ARCHITECTURE_INVITATION_ROLES: readonly string[] = ['user', 'reviewer', 'viewer', 'job_architect'];

type UseInviteTeamMemberParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
};

type UseInviteTeamMemberReturn = {
    readonly availableMethods: readonly InvitationMethod[] | undefined;
    readonly closeInviteDialog: () => void;
    readonly handleInvite: (data: InviteTeamMemberFormData) => Promise<void>;
    readonly isInviteDialogOpen: boolean;
    readonly openInviteDialog: () => void;
};

export const useInviteTeamMember = (params?: UseInviteTeamMemberParams): UseInviteTeamMemberReturn => {
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const canInvite = useHasRole('job_architect');
    const { showErrorMessageSnackbar, showSuccessSnackbar } = useSnackbars();
    const { inviteAndAssignToLevel, inviteAndAssignToRole } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const { data: invitationOptions } = useInvitationOptions(isInviteDialogOpen && canInvite);

    const inviteAndAssignMutation = useMutation({
        mutationFn: async (formData: InviteTeamMemberFormData) => {
            if (params === undefined) {
                throw new Error('Missing architecture context for invite-and-assign');
            }

            const request = {
                emailAddress: formData.email,
                firstName: formData.firstName,
                inviteLanguage: formData.inviteLanguage,
                lastName: formData.lastName,
                method: formData.method,
                roles: formData.roles as readonly string[],
            };

            const result =
                params.itemType === 'job-level'
                    ? await inviteAndAssignToLevel({
                          jobArchitectureId: params.architectureId,
                          levelId: params.itemId,
                          request,
                      })
                    : await inviteAndAssignToRole({
                          jobArchitectureId: params.architectureId,
                          request,
                          roleId: params.itemId,
                      });

            if (result.isErr) {
                throw result.error;
            }
        },
        onSuccess: async () => {
            if (params !== undefined) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: companySettingsKeys.organizationUsers() }),
                    queryClient.invalidateQueries({
                        queryKey:
                            params.itemType === 'job-level'
                                ? jobArchitectureKeys.assignableLevelReviewers(params.architectureId, params.itemId, '')
                                : jobArchitectureKeys.assignableRoleReviewers(params.architectureId, params.itemId, ''),
                    }),
                ]);
            }
        },
    });

    const openInviteDialog = (): void => {
        setIsInviteDialogOpen(true);
    };

    const closeInviteDialog = (): void => {
        setIsInviteDialogOpen(false);
    };

    const handleInvite = async (formData: InviteTeamMemberFormData): Promise<void> => {
        try {
            await inviteAndAssignMutation.mutateAsync(formData);
            showSuccessSnackbar('pages.company-settings.users-and-roles.success.invite-sent');
        } catch {
            showErrorMessageSnackbar('pages.company-settings.users-and-roles.errors.invite-failed');
        }
    };

    return {
        availableMethods: invitationOptions?.availableMethods,
        closeInviteDialog,
        handleInvite,
        isInviteDialogOpen,
        openInviteDialog,
    };
};
